import React from 'react'
import { xinyiInfo } from './content'
import type { Lang } from './types'

interface XinyiInfoSplitV2Props {
  currentLang: Lang
}

/**
 * XinyiInfoSplitV2
 * Right image (square) + left compact info table (address / hours / phone /
 * instagram). Reverse asymmetry vs XinyiAboutSplitV2 for rhythmic reading.
 */
const XinyiInfoSplitV2: React.FC<XinyiInfoSplitV2Props> = ({ currentLang }) => {
  const labels = {
    zh: { address: '地址', hours: '營業時間', phone: '聯絡電話', instagram: 'Instagram' },
    en: { address: 'Address', hours: 'Hours', phone: 'Phone', instagram: 'Instagram' },
  }[currentLang]

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:items-center md:gap-16">
          {/* Copy — left */}
          <div className="md:col-span-5 md:order-1">
            <p
              className="mb-5 text-[12px] tracking-[0.22em] text-black/65"
              style={{
                fontFamily: "'EB Garamond', serif",
                fontStyle: 'italic',
                fontWeight: 400,
              }}
            >
              {xinyiInfo.eyebrow[currentLang]}
            </p>

            <h2
              className="mb-8 text-black"
              style={{
                fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
                fontWeight: 400,
                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                letterSpacing: '0.04em',
                lineHeight: 1.25,
              }}
            >
              {xinyiInfo.title[currentLang]}
            </h2>

            <dl className="space-y-6 text-[14px] leading-loose text-black/80"
              style={{
                fontFamily: "'Noto Serif TC', sans-serif",
                fontWeight: 300,
                letterSpacing: '0.03em',
              }}
            >
              <div>
                <dt className="mb-1 text-[11px] tracking-[0.22em] text-black/55 uppercase">
                  {labels.address}
                </dt>
                <dd>
                  <a
                    href={xinyiInfo.addressUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border-b border-black/30 pb-0.5 transition-colors hover:border-black"
                  >
                    {xinyiInfo.address[currentLang]}
                  </a>
                </dd>
              </div>

              <div>
                <dt className="mb-1 text-[11px] tracking-[0.22em] text-black/55 uppercase">
                  {labels.hours}
                </dt>
                <dd>
                  {xinyiInfo.hours.map((line, i) => (
                    <div key={i}>{line[currentLang]}</div>
                  ))}
                </dd>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <dt className="mb-1 text-[11px] tracking-[0.22em] text-black/55 uppercase">
                    {labels.phone}
                  </dt>
                  <dd>
                    <a
                      href={`tel:${xinyiInfo.phone.replace(/\s/g, '')}`}
                      className="hover:text-black"
                    >
                      {xinyiInfo.phone}
                    </a>
                  </dd>
                </div>

                <div>
                  <dt className="mb-1 text-[11px] tracking-[0.22em] text-black/55 uppercase">
                    {labels.instagram}
                  </dt>
                  <dd>
                    <a
                      href={xinyiInfo.instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border-b border-black/30 pb-0.5 transition-colors hover:border-black"
                    >
                      {xinyiInfo.instagram}
                    </a>
                  </dd>
                </div>
              </div>
            </dl>

            <a
              href={xinyiInfo.addressUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex w-fit items-center text-sm tracking-[0.2em] text-black"
              style={{ fontFamily: "'EB Garamond', serif" }}
            >
              <span className="border-b border-black pb-1 transition-all duration-300 hover:tracking-[0.28em]">
                {currentLang === 'zh' ? 'Open in Google Maps →' : 'Open in Google Maps →'}
              </span>
            </a>
          </div>

          {/* Image — right */}
          <div className="md:col-span-7 md:order-2">
            <div className="relative aspect-square w-full overflow-hidden">
              <img
                src={xinyiInfo.image}
                alt={xinyiInfo.imageAlt[currentLang]}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default XinyiInfoSplitV2
